import { Option } from "@bluframe/blublocks"
import prepareComponent from "@bluframe/grapple/prepareComponent"
import {
  AutosuggestProps,
  Label,
  LabelStatus,
  LabelUpdateInput,
  VendorsSearchItem
} from "@bluframe/mightychroma-mightytool-frontend-types"
import useItemView, { UseItemViewReturn } from "hooks/useListItemView"
import { FormEventHandler, RefCallback, useCallback, useRef } from "react"

import Item from "./Item"

export interface Props {
  isAdd?: boolean
  isAdmin?: boolean
  isEditing?: boolean
  isEditor?: boolean
  item?: Label
  isSaveLoading?: boolean
  isVendorsSuggestionsLoading?: boolean
  onCancel: () => void
  onDelete?: () => void
  onEdit?: () => void
  onGetVendorsSuggestions: AutosuggestProps["onGetSuggestions"]

  onPublishToggle?: () => void

  onSave: (input: LabelUpdateInput) => void

  onSelectVendorId: (id: string, name: string) => void
  selectedVendorId?: string
  vendors: VendorsSearchItem[]
}

export interface ComponentProps
  extends
    Omit<
      Props,
      | "isEditing"
      | "isSaveLoading"
      | "onCancel"
      | "onDelete"
      | "onEdit"
      | "onPublishToggle"
      | "onSave"
    >,
    Omit<UseItemViewReturn, "item" | "refs"> {
  isActionButtonLoading?: boolean
  onSubmit: FormEventHandler
  urlRef: RefCallback<HTMLInputElement>
  statusRef: RefCallback<HTMLInputElement>
  statuses: Option[]
}

const usePrepareComponent = ({
  isAdd,
  isEditing,
  isSaveLoading,
  item,
  onCancel,
  onDelete,
  onEdit,
  onPublishToggle,
  onSave,
  ...props
}: Props): ComponentProps => {
  const {
    actionButtonOnClick,
    actionButtonLabel,
    isActionButtonLoading,
    isEditMode,
    isModalOpen,
    modalContent,
    onModalClose,
    onModalConfirmOpen,
    onModalDeleteConfirm,
    onModalPublishToggleConfirm,
    outlinedButtonLabel,
    outlinedButtonOnClick
  } = useItemView<Label>({
    fields: [],
    isAdd,
    isEditing,
    isSaveLoading,
    item,
    onCancel,
    onDelete,
    onEdit,
    onPublishToggle
  })

  const urlInputRef = useRef<HTMLInputElement | null>(null)
  const statusInputRef = useRef<HTMLInputElement | null>(null)

  const urlRef = useCallback<RefCallback<HTMLInputElement>>((node) => {
    urlInputRef.current = node
  }, [])

  const statusRef = useCallback<RefCallback<HTMLInputElement>>((node) => {
    statusInputRef.current = node
  }, [])

  const statuses = [
    { label: "Active", value: "active" as LabelStatus.ACTIVE },
    { label: "Inactive", value: "inactive" as LabelStatus.INACTIVE },
    { label: "Defunct", value: "defunct" as LabelStatus.DEFUNCT }
  ]

  const onSubmit: FormEventHandler = useCallback(
    (event) => {
      event.preventDefault()

      if (
        !urlInputRef.current?.value ||
        !statusInputRef.current?.value ||
        !props.selectedVendorId
      ) {
        return
      }

      onSave({
        status: statusInputRef.current.value as LabelStatus,
        url: urlInputRef.current.value,
        vendorId: props.selectedVendorId
      })
    },
    [onSave, props.selectedVendorId]
  )

  return {
    ...props,
    actionButtonLabel,
    actionButtonOnClick,
    isActionButtonLoading,
    isAdd,
    isEditMode,
    isModalOpen,
    item,
    modalContent,
    onModalClose,
    onModalConfirmOpen,
    onModalDeleteConfirm,
    onModalPublishToggleConfirm,
    onSubmit,
    outlinedButtonLabel,
    outlinedButtonOnClick,
    statusRef,
    statuses,
    urlRef
  }
}

export default prepareComponent<Props, ComponentProps>(usePrepareComponent)(
  Item
)
